import Errored from '../Errored';
import { Typography } from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import { v4 as uuidv4 } from 'uuid';
import GS from '../../grafsync/grafsync';
import BasicReadField from '../BasicReadField';
import SparqlProjectCard from './SparqlProjectCard';
import AreYouSure from '../../core/AreYouSure';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import DeleteIcon from '@material-ui/icons/Delete';
import PrimaryButton from '../PrimaryButton';
import SecondaryButton from '../SecondaryButton';
import LabelledSwitch from '../LabelledSwitch';
import LangConfirmationField from '../LangConfirmationField';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { globalStyles as styles } from '../style/gstheme';
import TitledPanel from '../TitledPanel';
import withGS from '../withGShoc';
import { MAX_DESCRIPTION_LENGTH, MAX_TITLE_LENGTH } from '../utils/constants';
import { getGraphFromProject, makeCancelable } from '../utils/utils';
import QueryParameterDetail from './QueryParameterDetail';
import AddParameterDialog from './AddParameterDialog';

class QueryDetail extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            areYouSure: false,
            existingVariables: [],
            sortedParameters: [],
            isLoading: true,
            isErrored: false
        };
        this.cancellable = null;
    }

    static getDerivedStateFromProps(props, state) {
        if (props.data._version !== state._version) {
            return {
                isLoading: true,
                _version: props.data._version,
            };
        }
        return null;
    }

    componentDidMount() {
        this.getSavedQueryParameters();
    }

    componentDidUpdate() {
        if (!this.state.isErrored && this.state.isLoading === true) {
            this.getSavedQueryParameters();
        }
    }
    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    getSavedQueryParameters = async () => {
        const queryParameter = this.props.data.queryParameter;
        try {
            GS.handle("get-saved-parameters");
            if (queryParameter && queryParameter.length > 0) {
                await this.doGetParameters().promise;
                let existingVariables = [];
                queryParameter.forEach(qp => {
                    const data = GS.data(qp, this.props.graph, false);
                    if (data) {
                        existingVariables.push(data.queryVariable);
                    }
                });
                existingVariables.sort();
                const sortedParameters = existingVariables.map(ev => queryParameter.find(qp => {
                    const data = GS.data(qp, this.props.graph, false);
                    return data.queryVariable === ev;
                }));
                this.setState({ existingVariables, sortedParameters, isLoading: false });
            }
            else {
                this.setState({ existingVariables: [], sortedParameters: [], isLoading: false });
            }
        }
        catch (e) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({ isErrored: e, errorMessage: "Could not get saved query parameters.", isLoading: false });
        }
    };

    doGetParameters() {
        this.cancellable = makeCancelable(GS.loadData(this.props.data.queryParameter, {}, this.props.graph, false));
        return this.cancellable;
    }

    checkIfSure = () => this.setState({ areYouSure: true });

    onNotSure = () => this.setState({ areYouSure: false });

    onSure = () => {
        this.setState({ areYouSure: false });
        this.props.onDeleteQuery(this.props.id);
    };

    onUpdate = (property) => (value) => {
        const lang = this.props.defaultLang;
        const exists = this.props.data[property] && this.props.data[property][lang];
        if (!exists) {
            this.props.onAdd(property, value, lang);
        }
        else this.props.onUpdate(property, value, lang, 0);
    };

    onDelete = (property) => (value) => this.props.onDelete(property, value, this.props.defaultLang);

    addParameter = async (data) => {
        const parameterIRI = "https://grafsync.graphifi.com/data/savedqueryparameter/" + uuidv4();
        let newParameter = {
            id: parameterIRI,
            type: "SavedQueryParameter",
            description: {
                [this.props.defaultLang]: data.description
            },
            ...(data.component) && { queryConstraint: data.component },
            queryReplacementType: data.expectedType,
            queryVariable: data.variable
        };
        await GS.insertResource(newParameter, parameterIRI, this.props.graph);
        this.props.onAdd("queryParameter", parameterIRI);
    };

    deleteParameter = (iri) => GS.deleteResource(iri, this.props.graph);

    updateExistingVariables = (existingVariables) => this.setState({ existingVariables });

    handleReportChange = (useReport) => {
        this.props.data.isReport !== undefined ?
            this.props.onUpdate('isReport', useReport.toString())
            :
            this.props.onAdd('isReport', true.toString());
    };

    render() {

        const { classes, data, onRevertQuery, disabled, projectType, defaultLang, updateQuery, reportOption,
            graph, queryVars, query, additionalProjects, project, existingComponents } = this.props;
        const { areYouSure, existingVariables, sortedParameters, isErrored } = this.state;
        const { title, description, isReport, id, queryType, queryProject, queryParameter } = data;

        if (isErrored) return <Errored data={isErrored} />;
        // Can't change query type
        const isReadOnly = this.props.isReadOnly || this.props.queryType !== queryType;
        const queryChanged = query && query !== data.query;
        const apLength = additionalProjects ? additionalProjects.length : 0;
        const qpLength = data.queryProject ? data.queryProject.length : 0;
        const additionalProjectsChanged = apLength !== qpLength;

        return (
            <div>
                <TitledPanel dataTest="query-title-panel" title="Title">
                    <LangConfirmationField
                        dataTest="query-title"
                        onUpdate={this.onUpdate("title")}
                        focus={false}
                        canBeDeleted={false}
                        maxLength={MAX_TITLE_LENGTH}
                        multiline={false}
                        text={title[defaultLang]}
                        lang={defaultLang}
                        isReadOnly={isReadOnly} />
                </TitledPanel>
                {queryChanged && <div data-test="query-changed" className={classes.incompleteData} style={{ marginTop: 6 }}>
                    <Typography variant="body2" align="center">
                        <WarningIcon data-test="query-warning" style={{ verticalAlign: 'middle' }} />
                        <span style={{ marginLeft: 8 }}>Query changed.</span>
                    </Typography>
                </div>}
                {additionalProjectsChanged && <div data-test="query-changed" className={classes.incompleteData} style={{ marginTop: 6 }}>
                    <Typography variant="body2" align="center">
                        <WarningIcon data-test="projects-warning" style={{ verticalAlign: 'middle' }} />
                        <span style={{ marginLeft: 8 }}>Additional projects changed.</span>
                    </Typography>
                </div>}
                <TitledPanel dataTest="query-description-panel" margin title="Description">
                    <LangConfirmationField
                        dataTest="query-description"
                        onUpdate={this.onUpdate("description")}
                        onDelete={this.onDelete("description")}
                        focus={false}
                        canBeDeleted={true}
                        maxLength={MAX_DESCRIPTION_LENGTH}
                        multiline={true}
                        allowEmpty={true}
                        text={description && description[defaultLang] ? description[defaultLang] : ""}
                        lang={defaultLang}
                        isReadOnly={isReadOnly} />
                </TitledPanel>
                <TitledPanel dataTest="query-iri-panel" margin title="IRI">
                    <BasicReadField dataTest="iri" fullWidth text={data.id} />
                </TitledPanel>
                {queryProject && queryProject.length > 0 &&
                    <TitledPanel dataTest="additional-saved-projects" margin title="Additional Projects">
                        {[...queryProject].sort().map(ap =>
                            <SparqlProjectCard
                                key={ap}
                                compact={true}
                                handleNotAvailable={true}
                                id={ap}
                                graph={getGraphFromProject(ap)} />
                        )}
                    </TitledPanel>
                }
                <TitledPanel margin title="Parameters" actions={
                    !isReadOnly && !queryChanged && queryVars.length > 0 && (!queryParameter || queryParameter.length < 5) ?
                        <AddParameterDialog
                            queryVars={queryVars}
                            onAddParameter={this.addParameter}
                            graph={graph}
                            components={existingComponents}
                            project={project}
                            projectType={projectType}
                            defaultLang={defaultLang}
                            existing={existingVariables}
                        />
                        :
                        null}>
                    {queryParameter && queryParameter.length > 0 &&
                        sortedParameters.map(qp => {
                            return <QueryParameterDetail
                                key={qp}
                                isReadOnly={isReadOnly}
                                defaultLang={defaultLang}
                                onDelete={this.deleteParameter}
                                queryVars={queryVars}
                                graph={graph}
                                id={qp} />;
                        })
                    }
                </TitledPanel>
                {reportOption && queryType === "SELECT" &&
                    <TitledPanel margin title="Options">
                        <LabelledSwitch
                            dataTest="options"
                            checked={isReport}
                            isReadOnly={isReadOnly}
                            disabled={isReadOnly || queryChanged}
                            handleChange={this.handleReportChange}
                            label="Use as a Report" />
                    </TitledPanel>
                }
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <div style={{ marginRight: 12 }}>
                        <PrimaryButton dataTest='update-query' disabled={disabled || isReadOnly || (!queryChanged && !additionalProjectsChanged)} onClick={() => updateQuery(id)}>Update</PrimaryButton>
                    </div>
                    <div>
                        <SecondaryButton dataTest='reset-query' disabled={!queryChanged && !additionalProjectsChanged} onClick={() => onRevertQuery(data)}>Reset</SecondaryButton>
                    </div>
                    <div style={{ paddingTop: 6 }}>
                        {!this.props.isReadOnly &&
                            <Tooltip placement="right" title='Delete this query'>
                                <IconButton
                                    data-test="delete-saved-query"
                                    size="small"
                                    onClick={this.checkIfSure}
                                    className={classes.deleteButton}>
                                    <DeleteIcon />
                                </IconButton>
                            </Tooltip>
                        }
                    </div>
                </div>
                {areYouSure && <AreYouSure
                    onSure={this.onSure}
                    onNotSure={this.onNotSure}
                    message={[
                        "Do you really want to delete this query?"
                    ]} />
                }
            </div>
        );
    }
}

QueryDetail.defaultProps = {
    isReadOnly: true,
    reportOption: false,
    queryVars: []
};

QueryDetail.propTypes = {
    data: PropTypes.object.isRequired,
    id: PropTypes.string.isRequired,
    defaultLang: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    queryType: PropTypes.string,
    queryVars: PropTypes.array,
    project: PropTypes.string.isRequired,
    query: PropTypes.string,
    existingComponents: PropTypes.array,
    projectType: PropTypes.string,
    graph: PropTypes.string.isRequired,
    additionalProjects: PropTypes.array,
    isReadOnly: PropTypes.bool,
    onRevertQuery: PropTypes.func.isRequired,
    onAdd: PropTypes.func,
    onUpdate: PropTypes.func,
    onDeleteQuery: PropTypes.func,
    onDelete: PropTypes.func,
    updateQuery: PropTypes.func.isRequired,
    reportOption: PropTypes.bool,
    classes: PropTypes.object
};

export default withStyles(styles)(withGS(QueryDetail));